import React from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import { PaymentRecord } from './hooks/useActivationPaymentSettings';
import { Loader2 } from 'lucide-react';

interface PaymentRecordsTableProps {
    paymentRecords: PaymentRecord[];
    isLoading?: boolean;
}

export default function PaymentRecordsTable({
    paymentRecords,
    isLoading = false,
}: PaymentRecordsTableProps) {
    const formatCurrency = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Payment Records</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="size-6 animate-spin text-muted-foreground" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (paymentRecords.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Payment Records</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        No payments found for the selected date range
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Payment Records ({paymentRecords.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Session ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Amount
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Paid At
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Claimed At
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Redemption Code
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Payment Intent
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-background divide-y divide-gray-200">
                            {paymentRecords.map((payment) => (
                                <tr
                                    key={payment.id}
                                    className="hover:bg-muted/50"
                                >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                                        {payment.photoSessionId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        <span className="font-semibold text-green-600">
                                            {formatCurrency(
                                                payment.costInCents,
                                            )}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        {payment.paidAt
                                            ? formatDateTime(payment.paidAt)
                                            : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        {payment.claimedAt
                                            ? formatDateTime(payment.claimedAt)
                                            : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                        <code className="bg-muted px-2 py-1 rounded text-xs">
                                            {payment.redemptionCode}
                                        </code>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                        {payment.paymentIntentId ? (
                                            <code className="bg-muted px-2 py-1 rounded text-xs">
                                                {payment.paymentIntentId.substring(
                                                    0,
                                                    20,
                                                )}
                                                ...
                                            </code>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {paymentRecords.length > 0 && (
                    <div className="mt-4 text-sm text-muted-foreground text-center">
                        Showing {paymentRecords.length} payment
                        {paymentRecords.length !== 1 ? 's' : ''}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
